import { Suspense, useMemo, useState } from 'react';
import uniq from 'lodash/uniq';

import useEvent from '@lyearn/core/hooks/useEvent';
import { Trans, useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { getIconSettingsWithRandomColor } from '@/components/ColorOrEmojiPicker/consts';
import Loader from '@/components/Loader';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { PreserveModalForm, usePreserveModalForm } from '@/components/PreserveModalForm';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { useCreateHabitMutation } from '@/modules/habits/graphql/mutations/generated/createHabit';
import { CreateMetricInput, KeyResultMetricEnum } from '@/types/schema';

import HabitEditor from '../HabitEditor';
import { CREATE_HABIT_INITIAL_VALUES } from './consts';
import { HabitCreateModalProps } from './types';

const HabitCreateModal: React.FC<HabitCreateModalProps> = (props) => {
  const { onClose } = props;
  const { onFormChange, onCloseModal, modalClosePrompt } = usePreserveModalForm({
    onClose,
  });
  const { t } = useTranslation(['Habits', 'Common']);
  const { id } = useUserContext();

  const { showSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const [{ fetching }, executeCreateHabit] = useCreateHabitMutation();
  const [submitRef, setSubmitRef] = useState<VoidFunction>(() => {});

  const onCreateHabit = useEvent((values: CreateMetricInput) => {
    executeCreateHabit({
      data: {
        ...values,
        userId: id,
        reminders: uniq(values.reminders),
        defaultTargetValue:
          values.measurementUnit === KeyResultMetricEnum.Toggle ? 1 : values.defaultTargetValue,
      },
    }).then((response) => {
      const habit = response.data?.createMetric?.metric;

      if (habit?.id) {
        showSnackbar({
          iconStatus: 'success',
          message: (
            <Trans i18nKey="Habits:Editor.CreateHabitSuccess" values={{ name: habit.name }} />
          ),
        });
        onClose();
      } else {
        showDefaultErrorSnackbar();
      }
    });
  });

  const initialValues = useMemo<CreateMetricInput>(() => {
    return { ...CREATE_HABIT_INITIAL_VALUES, iconSettings: getIconSettingsWithRandomColor() };
  }, []);

  return (
    <PreserveModalForm {...modalClosePrompt}>
      <Modal
        open
        action={{
          text: t('Common:Actions.Create'),
          onSubmit: submitRef,
          discardText: t('Common:Cancel'),
          isSubmitting: fetching,
        }}
        header={{
          title: t('Habits:Editor.CreateHabit'),
          description: t('Habits:Editor.CreateHabitDescription'),
        }}
        variant={ModalVariant.FullHeight}
        onClose={onCloseModal}>
        <Suspense fallback={<Loader />}>
          <HabitEditor
            initialValues={initialValues}
            setSubmitRef={setSubmitRef}
            onChange={onFormChange}
            onSubmit={onCreateHabit}
          />
        </Suspense>
      </Modal>
    </PreserveModalForm>
  );
};

export default HabitCreateModal;
